import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CCNavbar from '../../components/CCNavbar';
import CCFooter from '../../components/CCFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, PUBLISHER } from '../../data/legalContent';

function useCCTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'cardcycle');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

// Section order mirrors the approved CardCycle 0.3.0 Terms of Use
// (RC_PUBLIC_PRIVACY_TERMS_FAQ_0.3.0) one-for-one, so the public page and the
// static in-app legal text cannot drift apart.
const TOC = [
  { id: 'accept',    title: '1. Acceptance' },
  { id: 'service',   title: '2. The service' },
  { id: 'responsib', title: '3. Your responsibilities' },
  { id: 'noaccess',  title: '4. What CardCycle does not access' },
  { id: 'reminders', title: '5. Reminders and availability' },
  { id: 'localdata', title: '6. Your local data and exports' },
  { id: 'acceptable',title: '7. Acceptable use' },
  { id: 'ip',        title: '8. Intellectual property' },
  { id: 'liability', title: '9. Disclaimers and liability' },
  { id: 'changes',   title: '10. Changes' },
  { id: 'law',       title: '11. Law and jurisdiction' },
  { id: 'contact',   title: '12. Contact' },
];

export default function CCTerms() {
  useCCTheme();
  useDocumentMeta(routeMetaFor('/apps/cardcycle/terms'));

  return (
    <PageWrap testId="cc-terms-page">
      <CCNavbar />

      <header className="cc-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--cc-accent)' }}>CardCycle · Legal</div>
          <h1 className="reveal d1">Terms of Use</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="cc-terms-effective">Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.cardcycle.version}</p>
        </div>
      </header>

      <div className="wrap">
        <div className="cc-summary-box reveal">
          <div className="title">Key terms at a glance</div>
          <div className="cc-summary-grid">
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>CardCycle is a personal planning and record-keeping tool — not a bank, card issuer, payment processor, or financial adviser.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>It does not access SMS or messages, call logs, contacts, location, camera, microphone, or bank accounts.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Every record is one you entered or confirmed — CardCycle never marks a bill paid on its own.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Verify statement amounts, due dates and payments directly with your card issuer or bank.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Never store a full card number, CVV, PIN, OTP, or bank credentials in CardCycle.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Indian law governs; Ahmedabad courts have jurisdiction, subject to mandatory consumer rights.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--cc-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--cc-surf)', borderColor: 'var(--cc-border)' }}>
              <span className="legal-meta-tag" data-testid="cc-terms-publisher">Published by {PUBLISHER} · Ahmedabad, Gujarat, India · Support: {SUPPORT_EMAIL}</span>

              <section id="accept"><h2>1. Acceptance</h2>
                <p>By installing or using CardCycle, you agree to these Terms. If you do not agree, do not use the app.</p>
                <p>These Terms form an agreement between you and <strong>Tickle Apps</strong> (sole proprietorship, Ahmedabad, Gujarat, India), the publisher of CardCycle. Our <Link to={brandPath('cardcycle', '/privacy')}>Privacy Notice</Link> explains how CardCycle handles information on your device.</p>
              </section>

              <section id="service"><h2>2. The service</h2>
                <p>CardCycle is a personal planning and record-keeping tool for card statement cycles, dates, reminders, and records you choose to create or confirm.</p>
                <ul>
                  <li>It helps you track statement and payment dates.</li>
                  <li>It schedules on-device reminders when you allow notifications.</li>
                  <li>It lets you record statements, transactions, payment evidence, and planning actions.</li>
                </ul>
                <div className="callout alert">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span><strong>CardCycle is not a bank, card issuer, payment processor, broker, credit bureau, or financial adviser.</strong> It cannot move money, verify a payment, or guarantee that a reminder will be delivered, and it does not automatically mark a bill paid.</span>
                </div>
              </section>

              <section id="responsib"><h2>3. Your responsibilities</h2>
                <p>You are responsible for checking statement amounts, due dates, payment status, issuer changes, and payments directly with your card issuer or bank.</p>
                <p>You must not rely on CardCycle as your only method of tracking payment deadlines.</p>
              </section>

              <section id="noaccess"><h2>4. What CardCycle does not access</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-shield-halved" style={{ color: 'var(--cc-primary)' }}></i>
                  <span>CardCycle <strong>does not access SMS</strong>, call logs, contacts, location, camera, microphone, or bank accounts, and does not monitor messages.</span>
                </div>
                <p>Every record is one you entered or confirmed. CardCycle does not automatically create a paid action or make a payment.</p>
              </section>

              <section id="reminders"><h2>5. Reminders and availability</h2>
                <p>Reminder delivery may depend on device permissions, operating-system behaviour, battery settings, connectivity needed by the operating system, and other factors outside CardCycle&apos;s control.</p>
                <p>CardCycle does not guarantee any reminder, suggestion, estimate, or calculated date.</p>
              </section>

              <section id="localdata"><h2>6. Your local data and exports</h2>
                <p>You control local data and the files you export. Uninstalling, clearing app data, losing a device, or losing an export may permanently remove data.</p>
                <div className="callout warn">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span>Tickle Apps cannot recover local-only data. Keep your own exported files safe — if you lose an export, we cannot recover it for you.</span>
                </div>
              </section>

              <section id="acceptable"><h2>7. Acceptable use</h2>
                <ul>
                  <li>Do not use CardCycle to store full card numbers, CVV, PIN, OTP, bank credentials, or unlawful content.</li>
                  <li>Do not misuse, tamper with, or reverse engineer the app, except where applicable law permits.</li>
                  <li>Do not redistribute the app, except where applicable law permits.</li>
                </ul>
              </section>

              <section id="ip"><h2>8. Intellectual property</h2>
                <p>CardCycle software, name, visual assets, and content are owned by or licensed to Tickle Apps.</p>
                <p>You receive a personal, non-exclusive, revocable licence to use the app in accordance with these Terms.</p>
              </section>

              <section id="liability"><h2>9. Disclaimers and liability</h2>
                <p>CardCycle is provided on an <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> basis to the extent permitted by applicable law. Tickle Apps does not promise uninterrupted, error-free, timely, or suitable operation.</p>
                <p>Nothing in these Terms excludes consumer rights that cannot lawfully be excluded.</p>
              </section>

              <section id="changes"><h2>10. Changes</h2>
                <p>We may update these Terms for legal, security, or product reasons. Material changes will be identified through an updated effective date and, where appropriate, an in-app notice.</p>
              </section>

              <section id="law"><h2>11. Law and jurisdiction</h2>
                <p>These Terms are governed by the laws of <strong>India</strong>. Subject to mandatory consumer-protection rights, courts in <strong>Ahmedabad, Gujarat, India</strong> have jurisdiction.</p>
              </section>

              <section id="contact"><h2>12. Contact</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--cc-primary)' }}></i>
                  <span><strong>Email:</strong> <a href="mailto:support@tickleapps.com">support@tickleapps.com</a><br/>Tickle Apps · Ahmedabad, Gujarat, India</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>See also the <Link to={brandPath('cardcycle', '/privacy')}>CardCycle Privacy Notice</Link> and <Link to={brandPath('cardcycle', '/account-deletion')}>Data Deletion</Link>.</p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <CCFooter />
    </PageWrap>
  );
}
